/**
 * The job card's "Process" action (Capture tab, a rig capture's row; slice
 * 3.2): a small sheet that says where the capture is in the processing
 * queue, offers "on: automatic" or a computer that can do the work, and
 * starts it (startWork). Made for a phone: one column, 44 px targets, never
 * wider than the screen. The server decides (admin or rig_operator, the job
 * switched on, the computer able); the words come from
 * src/lib/startWorkModel.js.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { C, font, mono } from "../../../../lib/styles.js";
import { work } from "../../../../lib/workCallables.js";
// The console's modal focus rules: Tab stays inside, the page behind is inert, focus goes back to Process on close.
import { useModalDialog } from "../../../ingest/views/rigv2/dialogFocus.js";
import {
  AUTOMATIC, canStartCapture, captureResultText, captureStateLine, labelIn, pickerOf, pressIds, pressKey, processLabel, validTarget,
} from "../../../../lib/startWorkModel.js";

/** A rig capture's SN (snAllocator's canonical form): only those can be processed. */
export const CANONICAL_SN_RE = /^[a-z0-9]{6}\d{6}\d{4}$/;
export const processableSn = (row) => {
  const sn = row?.captureAttemptId || row?.captureattemptid || "";
  return row?.status === "captured" && CANONICAL_SN_RE.test(sn) ? sn : null;
};

/** The server's refusal in words (a crew login without the rig operator role is told so). */
export function startErrorText(e) {
  const code = String(e?.code || "");
  if (code.endsWith("permission-denied")) return "Your login cannot start processing (it needs the rig operator or admin role).";
  return e?.message || "That did not work. Try again.";
}

const btn = {
  minHeight: 44, padding: "0 16px", borderRadius: 6, fontFamily: font, fontSize: 14, cursor: "pointer",
  border: `1px solid ${C.border}`, background: C.surface, color: C.text,
};
const btnPrimary = { ...btn, background: C.text, borderColor: C.text, color: "#FFF" };

export default function ProcessCaptureSheet({ jobId, sn, title, onClose }) {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [target, setTarget] = useState(AUTOMATIC);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const live = useRef(true);
  const pressing = useRef(false);
  const frame = useRef(null);
  useModalDialog(frame);

  const load = useCallback(async () => {
    try {
      const o = await work.getWorkOptions({ jobId, sn });
      if (live.current) { setOptions(o); setLoadError(""); }
    } catch (e) {
      if (live.current) setLoadError(startErrorText(e));
    }
  }, [jobId, sn]);
  useEffect(() => {
    live.current = true;
    load();
    return () => { live.current = false; };
  }, [load]);

  const effective = validTarget(options, target);
  const labelOf = labelIn(options);
  const { choices, notOffered } = pickerOf(options);
  const capture = options?.capture ?? null;
  const startable = Boolean(options && options.jobListed && (options.kinds ?? []).includes("process") && canStartCapture(capture));

  const press = async () => {
    if (pressing.current) return;
    pressing.current = true;
    setBusy(true);
    setNote(null);
    // A press whose answer is unknown (a timeout) keeps its request id for the whole page (pressIds):
    // pressing again, even after closing and opening the sheet, asks about the same press, never twice.
    const key = pressKey(jobId, sn, effective);
    const requestId = pressIds.idFor(key);
    try {
      const r = await work.startWork({ kind: "process", jobId, sn, machineUid: effective || undefined, requestId });
      pressIds.settle(key, requestId);
      if (live.current) setNote({ kind: "ok", text: captureResultText(r, labelOf) });
    } catch (e) {
      pressIds.settle(key, requestId, e);
      if (live.current) setNote({ kind: "error", text: startErrorText(e) });
    } finally {
      pressing.current = false;
      if (live.current) setBusy(false);
      load();
    }
  };

  return (
    <div
      data-testid="process-sheet-scrim"
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(26,26,26,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={`Process ${title || sn}`} tabIndex={-1} ref={frame}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        style={{ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, width: 420, maxWidth: "100%", boxSizing: "border-box", padding: 18, fontFamily: font, outline: "none", display: "flex", flexDirection: "column", gap: 10 }}
      >
        <div style={{ fontSize: 16, fontWeight: 500, color: C.text }}>Process {title || "this capture"}</div>
        <div style={{ fontFamily: mono, fontSize: 12, color: C.muted, overflowWrap: "anywhere" }}>{sn}</div>
        {loadError && <div role="status" style={{ fontSize: 13, color: C.danger }}>{loadError}</div>}
        {!options && !loadError && <div style={{ fontSize: 13, color: C.muted }}>Reading the processing queue…</div>}
        {options && (
          <>
            <div data-testid="process-sheet-state" style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{captureStateLine(capture, labelOf)}</div>
            {!options.jobListed && (
              <div style={{ fontSize: 13, color: C.capture, lineHeight: 1.5 }}>This job is not switched on for processing: an admin switches it on (Rigs, Processing tab).</div>
            )}
            {options.jobListed && options.paused && (
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>Processing is paused: a started capture waits until an admin resumes it.</div>
            )}
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: C.muted }}>
              On
              <select
                aria-label="On" value={effective} disabled={busy} onChange={(e) => setTarget(e.target.value)}
                style={{ minHeight: 44, fontSize: 16, fontFamily: font, padding: "0 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, color: C.text, width: "100%", boxSizing: "border-box" }}
              >
                {choices.map((c) => <option key={c.value || "auto"} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            {notOffered.length > 0 && (
              <div data-testid="not-offered" style={{ fontSize: 12, color: C.muted, lineHeight: 1.4, overflowWrap: "anywhere" }}>
                Not offered: {notOffered.map((m) => `${m.label} (${m.why})`).join("; ")}.
              </div>
            )}
          </>
        )}
        {note && <div role="status" style={{ fontSize: 13, lineHeight: 1.5, color: note.kind === "error" ? C.danger : "#047857" }}>{note.text}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 4 }}>
          <button type="button" style={btn} onClick={onClose}>Close</button>
          <button type="button" style={{ ...btnPrimary, opacity: busy || !startable ? 0.5 : 1 }} disabled={busy || !startable} onClick={press}>
            {busy ? "Starting…" : processLabel(effective, labelOf)}
          </button>
        </div>
      </div>
    </div>
  );
}
